// ErrorBoundary.jsx
import React from "react";
import * as Sentry from "@sentry/react";
import { AlertCircle, Copy, Mail, RotateCw } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Config } from "./utils/config";

const ErrorFallback: any = ({ error, resetErrorBoundary }) => {
  const [sentryId, setSentryId] = React.useState<string | null>(null);

  // Capture the error with Sentry on mount
  React.useEffect(() => {
    const eventId = Sentry.captureException(error);
    console.log("Sentry Event ID:", eventId);
    setSentryId(eventId);
  }, [error]);

  const copyTraceId = async () => {
    if (sentryId) {
      try {
        await navigator.clipboard.writeText(sentryId);
        alert("Trace ID copied to clipboard");
      } catch (err) {
        console.error("Failed to copy trace ID", err);
      }
    }
  };

  const reportIssue = () => {
    const subject = encodeURIComponent(`Error Report - ID: ${sentryId}`);
    const body = encodeURIComponent(
      `Error ID: ${sentryId}\n\nError Details: ${error}\n\nPlease describe what you were doing when the error occurred:\n`
    );
    window.location.href = `mailto:${Config.env.app.support_email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <AlertCircle className="h-16 w-16 text-destructive animate-pulse" />
          </div>
          <CardTitle className="text-3xl">Something went wrong</CardTitle>
          <CardDescription className="text-base mt-2">
            We've encountered an unexpected error and our team has been notified
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Trace ID Section */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Trace ID:</span>
            </div>
            <div className="flex items-center justify-between text-center self-center">
              <code
                className="text-sm font-mono break-all text-muted-foreground text-center"
                onClick={copyTraceId}
              >
                {sentryId || "Generating ID..."}
              </code>
              <Copy
                className="h-4 w-4 mr-2 cursor-pointer"
                onClick={copyTraceId}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={() => {
                window.location.reload();
              }}
              className="flex-1 sm:flex-initial"
            >
              <RotateCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={reportIssue}
              className="flex-1 sm:flex-initial"
            >
              <Mail className="mr-2 h-4 w-4" />
              Report Issue
            </Button>
          </div>

          {/* Technical Details Accordion */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="details">
              <AccordionTrigger>Technical Details</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    <strong>Error Name:</strong> {error.name}
                  </p>
                  <p>
                    <strong>Message:</strong> {error.message}
                  </p>
                  {error.stack && (
                    <p className="whitespace-pre-wrap font-mono text-xs">
                      <strong>Stack:</strong> {error.stack}
                    </p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Error Boundary Component
const ErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      onReset={() => {
        window.location.reload();
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
